import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Linkedin } from "lucide-react";

const TEAM = [
  { name: "Daniel O'Brien", role: "Director, Software Engineering", img: "/team/dan.png", email: "dan.obrien@switchtt.co.uk", linkedin: "https://www.linkedin.com/in/dan-o-brien-542b1a129/" },
  { name: "Ollie Strong", role: "Director, Data & AI", img: "/team/ollie.png", email: "ollie.strong@switchtt.co.uk", linkedin: "https://www.linkedin.com/in/oliverstrong/" },
  { name: "Scott Elgar", role: "Director, Product & Design", img: "/team/scott.png", email: "scott.elgar@switchtt.co.uk", linkedin: "https://www.linkedin.com/in/scott-elgar/" },
];

const CONSULTANTS = [
  { name: "Luke Hunt", role: "Principal Consultant", img: "/team/luke.png", email: "luke.hunt@switchtt.co.uk", linkedin: "https://www.linkedin.com/in/lukehunt/" },
  { name: "Riley Hicks", role: "Senior Consultant", img: "/team/riley.png", email: "riley.hicks@switchtt.co.uk", linkedin: "https://www.linkedin.com/in/rileyhicks1/" },
  { name: "Mickey Fowler", role: "Lead DevOps Consultant", img: "/team/mickey.png", email: "mickey.fowler@switchtt.co.uk", linkedin: "https://www.linkedin.com/in/mickey-fowler-156b44b7/" },
  { name: "Harry Simmons", role: "Consultant", img: "/team/harry.png", email: "harry.simmons@switchtt.co.uk", linkedin: "https://www.linkedin.com/in/harry-simmons-a72652213/" },
];

const VALUES = [
  { k: "Craft", v: "We obsess over the details, from the first conversation to the 90-day check-in." },
  { k: "Candour", v: "Straight talk. We tell candidates and clients what they need to hear, not just what they want to hear." },
  { k: "Curiosity", v: "We go deep on technology. Understanding what's being built lets us find who should build it." },
  { k: "Continuity", v: "Most of our clients hire with us for years. That's the only metric that matters." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.92] max-w-5xl">
          A specialist in tech recruitment. Born in London, working across the UK, Europe &amp; the US.
        </h1>
        <p className="mt-10 text-white/70 text-lg md:text-xl font-body leading-relaxed max-w-3xl">
          We founded Switch because technical hiring deserved better. Too often recruitment feels like a numbers game, we built Switch to be the opposite: small, focused teams, deep specialism, long-term partnerships.
        </p>
      </section>

      {/* VALUES */}
      <section className="border-y border-white/10 py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10">
            <div className="md:col-span-4">
              <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.3em] mb-4">How we work</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white leading-[0.95]">Four C's we'll never compromise on.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-white/10">
            {VALUES.map((v, i) => (
              <div key={v.k} className={`p-10 ${i % 2 === 0 ? "md:border-r border-white/10" : ""} ${i < 2 ? "border-b border-white/10" : ""}`}>
                <div className="font-display font-black text-4xl text-[#3B82F6] tracking-tighter mb-3">{v.k}.</div>
                <p className="text-white/70 font-body leading-relaxed">{v.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.3em] mb-4">The team</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white">Directors.</h2>
            </div>
            <Link to="/contact" className="font-mono text-[11px] uppercase tracking-[0.2em] text-white hover:text-[#3B82F6] inline-flex items-center gap-2">
              Meet us in person <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {TEAM.map((m) => (
              <div key={m.name} data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`} className="group text-center">
                <div className="relative w-full aspect-square max-w-[320px] mx-auto">
                  <img src={m.img} alt={m.name} className="relative w-full h-full object-cover rounded-full ring-2 ring-white ring-offset-4 ring-offset-[#050505] group-hover:scale-[1.03] transition-transform duration-700 ease-out" />
                </div>
                <div className="mt-6">
                  <div className="font-display font-bold text-2xl text-white tracking-tight">{m.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#3B82F6] mt-2">{m.role}</div>
                  <div className="mt-4 flex items-center justify-center gap-3">
                    <a href={`mailto:${m.email}`} aria-label={`Email ${m.name}`} data-testid={`team-email-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`} className="w-9 h-9 border border-white/15 inline-flex items-center justify-center text-white/60 hover:text-[#3B82F6] hover:border-[#3B82F6] transition-colors">
                      <Mail size={14} />
                    </a>
                    <a href={m.linkedin} target="_blank" rel="noreferrer" aria-label={`LinkedIn ${m.name}`} data-testid={`team-linkedin-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`} className="w-9 h-9 border border-white/15 inline-flex items-center justify-center text-white/60 hover:text-[#3B82F6] hover:border-[#3B82F6] transition-colors">
                      <Linkedin size={14} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-24 md:mt-32 pt-16 border-t border-white/10">
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mb-14">Consultants.</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {CONSULTANTS.map((m) => (
                <div key={m.name} data-testid={`consultant-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`} className="group text-center">
                  <div className="relative w-full aspect-square max-w-[240px] mx-auto">
                    <img src={m.img} alt={m.name} className="relative w-full h-full object-cover rounded-full ring-2 ring-white ring-offset-4 ring-offset-[#050505] group-hover:scale-[1.03] transition-transform duration-700 ease-out" />
                  </div>
                  <div className="mt-5">
                    <div className="font-display font-bold text-lg md:text-xl text-white tracking-tight">{m.name}</div>
                    <div className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-[#3B82F6] mt-2">{m.role}</div>
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <a href={`mailto:${m.email}`} aria-label={`Email ${m.name}`} data-testid={`consultant-email-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`} className="w-8 h-8 border border-white/15 inline-flex items-center justify-center text-white/60 hover:text-[#3B82F6] hover:border-[#3B82F6] transition-colors">
                        <Mail size={12} />
                      </a>
                      <a href={m.linkedin} target="_blank" rel="noreferrer" aria-label={`LinkedIn ${m.name}`} data-testid={`consultant-linkedin-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`} className="w-8 h-8 border border-white/15 inline-flex items-center justify-center text-white/60 hover:text-[#3B82F6] hover:border-[#3B82F6] transition-colors">
                        <Linkedin size={12} />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.3em] mb-4">Our story</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white leading-[0.95]">Built for a different kind of recruitment.</h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-white/75 font-body leading-relaxed text-base md:text-lg">
            <p>Switch Tech Talent was founded in London with a clear idea: specialist hiring should be led by people who genuinely understand the technology. We keep our team small, our specialisms focused and our partnerships long.</p>
            <p>Today we support Series A scale-ups and listed enterprises across the UK, Europe and the US, building AI, Data, Software and Product teams that ship.</p>
            <p>We're proud that the majority of new briefs come from existing clients and candidates who've become clients. That's the compounding work of doing recruitment properly.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
